import { useRef, useEffect, Suspense, useCallback } from 'react'
import { extend, Canvas, useThree, useFrame } from '@react-three/fiber'
import { shaderMaterial } from '@react-three/drei'
import { Vector2, Vector3 } from 'three'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass'
import MouseOver from './MouseOver2'

const CustomMaterial = shaderMaterial(
	{
		iResolution: new Vector3(),
		mouse: new Vector2(),
		iTime: 0,
		red: 1.45,
		green: 1.14,
		blue: 0.4,
	},
	`
    varying vec2 vUv;
    void main()	{
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.);
    }
  `,
	`
    #define PI 3.14159265358979323846
    uniform vec2 iResolution;
    varying vec2 vUv;
    uniform sampler2D map;
    uniform float iTime;
    uniform float red;
    uniform float green;
    uniform float blue;
    uniform vec2 mouse;

    vec2 random2(vec2 st){
        st = vec2( dot(st,vec2(127.1,311.7)),
                  dot(st,vec2(269.5,183.3)) );
        return -1.0 + 2.0*fract(sin(st)*43758.5453123);
    }

    float noise(vec2 st) {
        vec2 i = floor(st);
        vec2 f = fract(st);

        vec2 u = smoothstep(0.,1.,f);

        return mix( mix( dot( random2(i + vec2(0.0,0.0) ), f - vec2(0.0,0.0) ),
                        dot( random2(i + vec2(1.0,0.0) ), f - vec2(1.0,0.0) ), u.x),
                    mix( dot( random2(i + vec2(0.0,1.0) ), f - vec2(0.0,1.0) ),
                        dot( random2(i + vec2(1.0,1.0) ), f - vec2(1.0,1.0) ), u.x), u.y);
    }

    vec4 permute(vec4 x){return mod(((x*34.0)+1.0)*x, 289.0);}
    vec2 fade(vec2 t) {return t*t*t*(t*(t*6.0-15.0)+10.0);}

    float cnoise(vec2 P){
        vec4 Pi = floor(P.xyxy) + vec4(0.0, 0.0, 1.0, 1.0);
        vec4 Pf = fract(P.xyxy) - vec4(0.0, 0.0, 1.0, 1.0);
        Pi = mod(Pi, 289.0); // To avoid truncation effects in permutation
        vec4 ix = Pi.xzxz;
        vec4 iy = Pi.yyww;
        vec4 fx = Pf.xzxz;
        vec4 fy = Pf.yyww;
        vec4 i = permute(permute(ix) + iy);
        vec4 gx = 2.0 * fract(i * 0.0243902439) - 1.0; // 1/41 = 0.024...
        vec4 gy = abs(gx) - 0.5;
        vec4 tx = floor(gx + 0.5);
        gx = gx - tx;
        vec2 g00 = vec2(gx.x,gy.x);
        vec2 g10 = vec2(gx.y,gy.y);
        vec2 g01 = vec2(gx.z,gy.z);
        vec2 g11 = vec2(gx.w,gy.w);
        vec4 norm = 1.79284291400159 - 0.85373472095314 * 
          vec4(dot(g00, g00), dot(g01, g01), dot(g10, g10), dot(g11, g11));
        g00 *= norm.x;
        g01 *= norm.y;
        g10 *= norm.z;
        g11 *= norm.w;
        float n00 = dot(g00, vec2(fx.x, fy.x));
        float n10 = dot(g10, vec2(fx.y, fy.y));
        float n01 = dot(g01, vec2(fx.z, fy.z));
        float n11 = dot(g11, vec2(fx.w, fy.w));
        vec2 fade_xy = fade(Pf.xy);
        vec2 n_x = mix(vec2(n00, n01), vec2(n10, n11), fade_xy.x);
        float n_xy = mix(n_x.x, n_x.y, fade_xy.y);
        return 2.3 * n_xy;
    }

    void main()
    {
        vec2 uv = vUv;
        vec2 aspect = vec2(iResolution.x / iResolution.y,1.);

        vec2 blob = (uv-aspect / 2.)*3.5;
        float rotate = -iTime*0.16;
        blob *= mat2(cos(rotate), -sin(rotate), sin(rotate), cos(rotate));
        blob += 5. * blob * smoothstep(2.5,0.,length(blob)) * -3.;
        vec3 col = vec3(cnoise(uv * 0.2 + vec2(0., iTime*0.5))) * vec3(0.9,0.2,0.5)*0.85+0.85;
        col.r += cnoise(blob * 0.8 + vec2(1., iTime*0.15)) * 4.5;
        col.g += cnoise(blob * 0.25 + vec2(1., iTime * 0.18)) * 4.;
        col.b += cnoise(blob * 0.15 + vec2(1., iTime * 0.25)) * 0.5;

        float clod = 1. + cnoise((blob * 5.5 + vec2(cnoise(uv * 3.3), cnoise(uv * 7.4 + vec2(-iTime * 0.1, 0.))) * 0.5)*4.+vec2(iTime * 0.4, 0.));
        col.r += (clod - 0.35) * 1.45 * step(1.4, clod);
        clod = cnoise((blob * 0.5 + vec2(noise(uv * 3.3 + 0.05), noise(uv * 7.4 + vec2(-iTime * 0.1, 0.) + 0.1)) * 0.5) * 4.+vec2(iTime * 0.4, 0.));
        col.g += (clod-1.35) * 1.75 * step(0.17,clod);
        clod = noise((blob * 0.5 + vec2(noise(uv * 31.3 + 0.07), noise(uv * 7.4 + vec2(-iTime * 0.1, 0.) + 0.2)) * 0.5) * 4. + vec2(iTime * 0.4, 0.));
        col.b += (clod - 0.4) * 1.4 * step(0.1, clod);

		vec4 final = vec4(col * 0.5 + 0.5, 1.);
		vec3 greyScale = vec3(.15, .15, .15);
    	gl_FragColor = vec4( vec3(dot( final.rgb, greyScale)), final.a);
		//gl_FragColor = final;
    }
`
)

extend({ CustomMaterial, EffectComposer, RenderPass, ShaderPass })

function Effects() {
	const { gl, scene, camera, size } = useThree()
	const composer = useRef()
	const ref = useRef()
	const mouseRef = useRef()

	useEffect(() => {
		composer.current.setSize(size.width, size.height)
	}, [size])

	const mouse = useRef({ x: 0.5, y: 0.5 })
	const prevMouse = useRef({ x: 0.5, y: 0.5 })
	const speed = useRef(0)
	const targetSpeed = useRef(0)
	const followMouse = useRef({ x: 0.5, y: 0.5 })
	const onMouseMove = useCallback(({ clientX: x, clientY: y }) => {
		mouse.current = {
			x: x / window.innerWidth,
			y: 1 - y / window.innerHeight,
		}
	}, [])

	const onTouchMove = useCallback((e) => {
		const x = e.touches[0].clientX
		const y = e.touches[0].clientY
		mouse.current = {
			x: x / window.innerWidth,
			y: 1 - y / window.innerHeight,
		}
	}, [])

	useEffect(() => {
		window.addEventListener('mousemove', onMouseMove)
		window.addEventListener('touchmove', onTouchMove)
		return () => {
			window.removeEventListener('mousemove', onMouseMove)
			window.removeEventListener('touchmove', onTouchMove)
		}
	}, [])

	const getSpeed = () => {
		speed.current = Math.sqrt(
			(prevMouse.current.x - mouse.current.x) ** 2 +
				(prevMouse.current.y - mouse.current.y) ** 2
		)

		targetSpeed.current -= 0.1 * (targetSpeed.current - speed.current)
		followMouse.current.x -= 0.1 * (followMouse.current.x - mouse.current.x)
		followMouse.current.y -= 0.1 * (followMouse.current.y - mouse.current.y)

		prevMouse.current.x = mouse.current.x
		prevMouse.current.y = mouse.current.y
	}

	useFrame(({ clock }) => {
		getSpeed()

		mouseRef.current.material.uniforms.time.value = clock.getElapsedTime()
		mouseRef.current.material.uniforms.uMouse.value = new Vector2(
			followMouse.current.x,
			followMouse.current.y
		)
		mouseRef.current.material.uniforms.uVelo.value = Math.min(
			targetSpeed.current,
			0.05
		)

		targetSpeed.current *= 0.999

		composer.current.render()
	}, 1)

	return (
		<effectComposer ref={composer} args={[gl]}>
			<renderPass attachArray='passes' args={[scene, camera]} />
			<shaderPass
				ref={mouseRef}
				attachArray='passes'
				args={[MouseOver]}
				material-uniforms-time-value={0}
				material-uniforms-resolution-value={[1, 1]}
			/>
		</effectComposer>
	)
}

function Scene() {
	const ref = useRef()
	const { viewport } = useThree()
	useFrame(({ mouse }) => {
		ref.current.uniforms.iTime.value += 0.01
		ref.current.uniforms.mouse.value = mouse
	})
	return (
		<mesh>
			<planeBufferGeometry args={[viewport.width, viewport.height]} />
			<customMaterial ref={ref} attach='material' iResolution={[1, 1]} />
		</mesh>
	)
}
function App() {
	return (
		<Canvas>
			<Suspense fallback={null}>
				<Scene />
				<Effects />
			</Suspense>
		</Canvas>
	)
}

export default App
